/**
 * The new shell's bell (prototype v108): unread count for the role, the three most recent unread alerts and open
 * Flags, and "Open Notification Center". Read state comes from the same per-viewer marks the Center keeps.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/admin/ui/popover';
import { Button } from '@/components/admin/ui/button';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { systemItem, staffItem, flagItem, visibleTo, sortNewest, readMark } from '@/lib/notifications/center';
import { stagingFetch } from './api';
import { useNotificationReads } from './useNotificationReads';
import { STAGING_ROOT } from '@/lib/staging/roleNav';

export default function StagingBell({ role, onCount }: { role: string; onCount?: (n: number) => void }) {
  const { user } = useAdminAuth();
  const [rows, setRows] = useState<{ system: any[]; flags: any[]; staff: any[] }>({ system: [], flags: [], staff: [] });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { marks } = useNotificationReads(user?.id);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const [n, f, s] = await Promise.all([
        supabaseAdmin.from('role_notifications').select('id, account_name, severity, type, created_at, target_role, message, booking_id, status').neq('status', 'cleared').order('created_at', { ascending: false }).limit(60),
        supabaseAdmin.from('flagged_accounts').select('id, user_id, reason, notes, severity, status, assigned_to, assigned_to_role, flagged_by, created_at, resolved_at').neq('status', 'resolved').order('created_at', { ascending: false }).limit(30),
        stagingFetch('/api/admin/staging/notifications/feed?limit=60').catch(() => ({ items: [] }))
      ]);
      if (!cancelled) setRows({ system: n.data || [], flags: f.data || [], staff: s?.items || [] });
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [role, open]);

  const items = useMemo(() => {
    const all = [...rows.system.map((r) => systemItem(r, { read: readMark(marks, `system:${r.id}`, r.created_at) })), ...rows.staff.map((r) => staffItem(r, { read: readMark(marks, `system:${r.id}`, r.created_at) })), ...rows.flags.map((r) => ({ ...flagItem(r), read: readMark(marks, `flag:${r.id}`, r.created_at) }))];
    return sortNewest(visibleTo(all, role)).filter((i: any) => !i.read);
  }, [rows, role, marks]);
  useEffect(() => { onCount?.(items.length); }, [items.length, onCount]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={`Notifications, ${items.length} unread`}>
          <Bell className="h-4 w-4" />
          {items.length > 0 && <span className="absolute top-0 right-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">{items.length > 99 ? '99+' : items.length}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="px-4 pt-3 pb-2"><div className="font-semibold">Notifications</div><div className="text-xs text-muted-foreground">Recent unread alerts</div></div>
        <div className="px-3 space-y-2">
          {items.length === 0 ? <p className="text-sm text-muted-foreground px-1 pb-2">Nothing unread.</p> : items.slice(0, 3).map((it: any) => (
            // Traci, 28 Sep 2026: a preview opens that notification in the Notification Center
            <button key={it.id} type="button" className="block w-full rounded-lg border bg-card p-3 text-left hover:bg-accent" onClick={() => { setOpen(false); navigate(`${STAGING_ROOT}/notifications?open=${encodeURIComponent(it.id)}`); }}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{it.kind === 'flag' ? 'assigned_flag' : 'system'}</div>
              <div className="text-sm font-semibold mt-0.5">{it.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{it.message}</div>
            </button>
          ))}
        </div>
        <div className="border-t mt-3 px-4 py-2.5 text-center text-sm"><Link to={`${STAGING_ROOT}/notifications`} className="text-primary hover:underline" onClick={() => setOpen(false)}>Open Notification Center →</Link></div>
      </PopoverContent>
    </Popover>
  );
}
